import { forwardRef } from "react";
import { Tooltip } from "@heroui/react";
import { Button, ButtonProps, ModalProps } from "@heroui/react";
import { LimeAlertProps } from "./LimeAlert";
import { useConfirmModal } from "./useConfirmModal";

export const AlertButton = forwardRef(
  (
    {
      children,
      title,
      content,
      errorMessages,
      tooltip,
      confirmProps,
      cancelProps,
      buttonProps,
      modalProps,
      onBeforeOpen,
      hideTrigger,
      isOpen: isOpenProp,
      onOpenChange: onOpenChangeProp,
    }: {
      children?: React.ReactNode;
      title: string;
      content: React.ReactNode;
      errorMessages?: LimeAlertProps[];
      tooltip?: string;
      confirmProps: {
        label?: string;
        icon?: React.ReactNode;
        onClick: () => Promise<void>;
        isLoading?: boolean;
        isDisabled?: boolean;
        dataIdentifier?: string;
      };
      cancelProps?: {
        label?: string;
        icon?: React.ReactNode;
        onClick?: () => void;
      };
      buttonProps?: ButtonProps & {
        dataIdentifier?: string;
      };
      modalProps?: Omit<ModalProps, "children">;
      onBeforeOpen?: () => void;
      hideTrigger?: boolean;
      isOpen?: boolean;
      onOpenChange?: (isOpen: boolean) => void;
    },
    ref: React.Ref<HTMLButtonElement>,
  ) => {
    const { open, modal } = useConfirmModal({
      title,
      content,
      errorMessages,
      confirmProps,
      cancelProps,
      modalProps,
      isOpen: isOpenProp,
      onOpenChange: onOpenChangeProp,
    });

    const handleOpen = () => {
      if (onBeforeOpen) {
        onBeforeOpen();
        // Defer the open by a microtask so any state set in `onBeforeOpen`
        // (which happens synchronously) commits before the modal mounts.
        // Preserves the original AlertButton behaviour.
        queueMicrotask(open);
        return;
      }

      open();
    };

    const trigger = (
      <SharedButton
        color="primary"
        {...buttonProps}
        data-identifier={buttonProps?.dataIdentifier}
        onPress={handleOpen}
        ref={ref}
      >
        {children}
      </SharedButton>
    );

    return (
      <>
        {!hideTrigger ? (
          tooltip ? (
            <Tooltip content={tooltip}>{trigger}</Tooltip>
          ) : (
            trigger
          )
        ) : null}
        {modal}
      </>
    );
  },
);

const SharedButton = ({
  children,
  onPress,
  ref,
  ...props
}: ButtonProps & { dataIdentifier?: string } & {
  onPress: () => void;
  ref: React.Ref<HTMLButtonElement>;
}) => {
  return (
    <Button
      color="primary"
      {...props}
      data-identifier={props?.dataIdentifier}
      onPress={onPress}
      ref={ref}
    >
      {children}
    </Button>
  );
};

AlertButton.displayName = "AlertButton";
